import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { notify } from 'src/lib/notify';
import { COMMODITY_OPTIONS_QUERY } from 'src/modules/entities/commodities';
import type {
  CreateCommodityInput,
  CreateCommodityLabelInput,
  CreateCommodityMutation,
  CreateCommodityMutationVariables,
  CreateCommoditySizeInput,
  CreateCommodityStyleInput,
  CreateCommodityUnitInput,
  CreateLabelMutation,
  CreateLabelMutationVariables,
  CreateProductInput,
  CreateProductMutation,
  CreateProductMutationVariables,
  CreateSizeMutation,
  CreateSizeMutationVariables,
  CreateStyleMutation,
  CreateStyleMutationVariables,
  CreateUnitMutation,
  CreateUnitMutationVariables,
  ProductReservationsQuery,
  ProductReservationsQueryVariables,
  UpdateProductInput,
  updateProduct,
  updateProductVariables,
} from 'types/graphql';

import { COMMODITY_CATALOG_QUERY } from './hooks/useCommodities';
import { COMMODITY_CATALOG_PRODUCTS_PAGE_QUERY } from './hooks/useCommoditiesPage';

export type ProductReservation = NonNullable<NonNullable<ProductReservationsQuery['productReservations']>[number]>;

/**
 * Refetch BOTH documents: a product write moves the page's rows AND the metadata's
 * per-commodity `productCount`. `refetchQueries` replays each active query with its own
 * variables, so the page refetches the window on screen, not page one.
 */
const CATALOG_REFETCH_QUERIES = [COMMODITY_CATALOG_QUERY, COMMODITY_CATALOG_PRODUCTS_PAGE_QUERY];

const CREATE_PRODUCT = gql`
  mutation CreateProductMutation($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
    }
  }
`;

export function useCreateProduct() {
  const [createProduct] = useMutation<CreateProductMutation, CreateProductMutationVariables>(CREATE_PRODUCT, {
    refetchQueries: CATALOG_REFETCH_QUERIES,
    onCompleted: () => {
      notify.success('Product created');
    },
  });

  return useCallback((input: CreateProductInput) => createProduct({ variables: { input } }), [createProduct]);
}

const UPDATE_PRODUCT = gql`
  mutation updateProduct($id: Int!, $input: UpdateProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      productCode
      costBasis
      commodityStyle {
        id
        name
      }
      commoditySize {
        id
        name
      }
      commodityUnit {
        id
        name
      }
      commodityLabel {
        id
        name
      }
    }
  }
`;

export function useUpdateProduct() {
  const [updateProduct] = useMutation<updateProduct, updateProductVariables>(UPDATE_PRODUCT, {
    refetchQueries: CATALOG_REFETCH_QUERIES,
    onCompleted: () => {
      notify.success('Product updated');
    },
  });

  return useCallback(
    (id: number, input: UpdateProductInput) => updateProduct({ variables: { id, input } }),
    [updateProduct]
  );
}

const CREATE_COMMODITY_STYLE = gql`
  mutation CreateStyleMutation($input: CreateCommodityStyleInput!) {
    createCommodityStyle(input: $input) {
      id
    }
  }
`;

export function useCreateCommodityStyle() {
  const [createCommodityStyle] = useMutation<CreateStyleMutation, CreateStyleMutationVariables>(CREATE_COMMODITY_STYLE);

  return useCallback(
    (input: CreateCommodityStyleInput) => createCommodityStyle({ variables: { input } }),
    [createCommodityStyle]
  );
}

const CREATE_COMMODITY_SIZE = gql`
  mutation CreateSizeMutation($input: CreateCommoditySizeInput!) {
    createCommoditySize(input: $input) {
      id
      name
    }
  }
`;

export function useCreateCommoditySize() {
  const [createCommoditySize] = useMutation<CreateSizeMutation, CreateSizeMutationVariables>(CREATE_COMMODITY_SIZE);

  return useCallback(
    (input: CreateCommoditySizeInput) => createCommoditySize({ variables: { input } }),
    [createCommoditySize]
  );
}

const CREATE_COMMODITY_UNIT = gql`
  mutation CreateUnitMutation($input: CreateCommodityUnitInput!) {
    createCommodityUnit(input: $input) {
      id
      name
    }
  }
`;

export function useCreateCommodityUnit() {
  const [createCommodityUnit] = useMutation<CreateUnitMutation, CreateUnitMutationVariables>(CREATE_COMMODITY_UNIT);

  return useCallback(
    (input: CreateCommodityUnitInput) => createCommodityUnit({ variables: { input } }),
    [createCommodityUnit]
  );
}

const CREATE_COMMODITY_LABEL = gql`
  mutation CreateLabelMutation($input: CreateCommodityLabelInput!) {
    createCommodityLabel(input: $input) {
      id
      name
    }
  }
`;

export function useCreateCommodityLabel() {
  const [createCommodityLabel] = useMutation<CreateLabelMutation, CreateLabelMutationVariables>(CREATE_COMMODITY_LABEL);

  return useCallback(
    (input: CreateCommodityLabelInput) => createCommodityLabel({ variables: { input } }),
    [createCommodityLabel]
  );
}

const CREATE_COMMODITY = gql`
  mutation CreateCommodityMutation($input: CreateCommodityInput!) {
    createCommodity(input: $input) {
      id
      name
    }
  }
`;

/** The add-commodity modal's write. The modal keeps its own toasts and close behavior. */
export function useCreateCommodity() {
  const [mutate, { loading, error }] = useMutation<CreateCommodityMutation, CreateCommodityMutationVariables>(
    CREATE_COMMODITY,
    // An open commodity picker lists the new commodity too.
    { refetchQueries: [COMMODITY_CATALOG_QUERY, COMMODITY_OPTIONS_QUERY] }
  );

  const create = useCallback((input: CreateCommodityInput) => mutate({ variables: { input } }), [mutate]);

  return { create, loading, error };
}

const PRODUCT_RESERVATIONS_QUERY = gql`
  query ProductReservationsQuery($id: Int!) {
    productReservations(id: $id) {
      slug
      status
      type
      createdAt
      warehouseId
    }
  }
`;

export function useFindProductReservations() {
  const [findProductReservations] = useLazyQuery<ProductReservationsQuery, ProductReservationsQueryVariables>(
    PRODUCT_RESERVATIONS_QUERY
  );

  return useCallback(
    async ({ id }: { id: number }) => {
      const result = await findProductReservations({ variables: { id } });
      return result;
    },
    [findProductReservations]
  );
}
